import LockIcon from '@mui/icons-material/Lock';
import OpenInNew from '@mui/icons-material/OpenInNew';
import {
  Avatar,
  Box,
  colors,
  IconButton,
  Paper,
  styled,
  SvgIcon,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
} from '@mui/material';

import { visuallyHidden } from '@mui/utils';
import * as React from 'react';
import { useCallback, useRef } from 'react';
import { Button } from '~/components/Button';
import { FaviconImage } from '~/components/FaviconImage';
import { Link } from '~/components/Link';
import { LogoPoweredByLink } from '~/components/LogoPoweredByLink';
import BlurredUpgradeModal from '~/components/SearchResultsTable/BlurredUpgradeModal';
import { TagChip } from '~/components/TagChip';
import { useUser } from '~/hooks/useUser';
import {
  domain as domainColumn,
  employees as employeesColumn,
  rank as rankColumn,
  spendMin as spendMinColumn,
  updatedAt as updatedAtColumn,
} from '~/scripts/columns';
import { getDaysAgo } from '~/scripts/getDaysAgo';
import { numberAbbreviation } from '~/scripts/numberAbbreviation';
import { Search } from '~/types/search';
import { BlurWrapper } from './BlurWrapper';
import { Order, RowData, SortableFields } from './types';
import { CopyIconButton } from '~/components/CopyInputButton/CopyInputButton';
import type { TagFilterKeys } from '@shared/filters';
const columns = [
  domainColumn,
  spendMinColumn,
  employeesColumn,
  rankColumn,
  updatedAtColumn,
];

// fields that can only be sorted ascending
const ascendingOnlyFields: (keyof RowData)[] = ['rank'];

const isAscendingOnly = (field: keyof RowData) => {
  return ascendingOnlyFields.includes(field);
};

// ascending-only fields can't be toggled once they're the active sort
const isSortToggleable = (field: keyof RowData, sort: SortableFields) => {
  return !(sort === field && isAscendingOnly(field));
};

const getSortDirection = (
  field: keyof RowData,
  sort: SortableFields,
  order: Order
): Order => {
  return sort === field && !isAscendingOnly(field) ? order : 'asc';
};

interface SearchResultsTableProps {
  sites: Search.GetSearchResponse['sites'];
  highlighting?: Search.GetSearchResponse['highlighting'];
  total: Search.GetSearchResponse['total'];
  order: Order;
  showSnippets: boolean;
  sort: SortableFields;
  onSortChange: (sort: string) => void;
  sx?: SxProps<Theme>;
  setPage: (page: number) => void;
  page: number;
}

const StyledSnippet = styled('pre')`
  white-space: pre-wrap;
  word-break: break-all;
  background-color: #eeeeee;
  border-radius: 6px;
  padding: 27px 10px 10px;
  margin: 10px 0;
  position: relative;

  &:before {
    content: 'Code Snippet';
    position: absolute;
    margin-top: -20px;
    font-size: 10px;
    left: 11px;
  }
  hl {
    background-color: yellow;
  }
`;

interface SnippetRowProps {
  domain: string;
  snippet: string;
  columnsLength: number;
}

const SnippetRow = React.memo(
  ({ domain, snippet, columnsLength }: SnippetRowProps) => {
    return (
      <TableRow key={`${domain}-snippet`}>
        <TableCell colSpan={columnsLength}>
          <StyledSnippet
            dangerouslySetInnerHTML={{
              __html: snippet,
            }}
          />
        </TableCell>
      </TableRow>
    );
  }
);

SnippetRow.displayName = 'SnippetRow';

interface DomainCellProps {
  domain: string;
  blurred: boolean;
  url: string;
  tags?: TagFilterKeys[];
}

const DomainCell = React.memo(
  ({ domain, url, tags, blurred }: DomainCellProps) => {
    const user = useUser();
    const domainHref = React.useMemo(() => {
      if (blurred) {
        return user.isGuest
          ? '/signup?reason=search.blurred'
          : '/pricing?reason=search.blurred';
      }
      return `/domains/${domain}`;
    }, [blurred, user.isGuest, domain]);

    const externalLinkHref = React.useMemo(() => {
      if (blurred) {
        return user.isGuest
          ? '/signup?reason=search.blurred'
          : '/pricing?reason=search.blurred';
      }
      return (url || `https://${domain}`) + '?&utm_source=www.nerdydata.com';
    }, [blurred, user.isGuest, domain, url]);
    return (
      <TableCell align="left" sx={{ overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'start',
            overflow: 'visible',
            minWidth: 0,
            flexWrap: 'wrap',
          }}>
          <Box sx={{ overflow: 'hidden', minWidth: 0, flexShrink: 1 }}>
            <BlurWrapper blurred={blurred}>
              <Button
                variant="text"
                href={domainHref}
                target={blurred ? undefined : '_blank'}
                sx={{
                  py: 0,
                  pr: 0,
                  mt: -0.25,
                  minWidth: 200,
                  verticalAlign: 'middle',
                  maxWidth: '100%',
                  justifyContent: 'flex-start',
                }}
                size="small"
                startIcon={
                  <Avatar
                    sx={{
                      backgroundColor: 'transparent',
                      mx: -1,
                    }}
                    variant="square"
                    alt={`${domain} logo`}>
                    {!blurred ? (
                      <FaviconImage domain={domain} height="25" width="25" />
                    ) : (
                      <LockIcon sx={{ color: colors.grey[500] }} />
                    )}
                  </Avatar>
                }
                title={
                  blurred ? undefined : `List of technologies used on ${domain}`
                }>
                {domain}
              </Button>
            </BlurWrapper>
          </Box>
          <Box
            display="flex"
            gap={0}
            alignItems="center"
            justifyContent="flex-start"
            alignContent="flex-start"
            flexShrink={0}>
            <Tooltip title={blurred ? '' : `Open ${domain} in new window`}>
              <IconButton
                LinkComponent={(props) => <Link {...props} hideExternalIcon />}
                sx={{ mr: -1 }}
                target={blurred ? undefined : '_blank'}
                href={externalLinkHref}>
                <OpenInNew fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Copy ${domain} to clipboard`}>
              <span>
                <CopyIconButton value={domain} size="small" sx={{}} />
              </span>
            </Tooltip>
            {tags &&
              tags.length > 0 &&
              Array.from(new Set(tags)).map((tag, index) => {
                return (
                  <TagChip
                    key={tag}
                    tag={tag}
                    sx={{ ml: index === 0 ? 0 : 0.5, verticalAlign: 'middle' }}
                  />
                );
              })}
          </Box>
        </Box>
      </TableCell>
    );
  }
);

DomainCell.displayName = 'DomainCell';

interface DataCellProps {
  value: string | number;
  blurred: boolean;
  align?: 'left' | 'right' | 'center';
}

const DataCell = React.memo(
  ({ value, blurred, align = 'right' }: DataCellProps) => {
    return (
      <TableCell align={align}>
        <BlurWrapper blurred={blurred}>{value}</BlurWrapper>
      </TableCell>
    );
  }
);

DataCell.displayName = 'DataCell';

interface TableRowProps {
  row: RowData;
  showSnippets: boolean;
  snippet: string | null;
}

const TableRowComponent = React.memo(
  React.forwardRef<HTMLTableRowElement, TableRowProps>(
    ({ row, showSnippets, snippet }, ref) => (
      <>
        <TableRow
          tabIndex={-1}
          key={`${row.domain}-${row.updated_at}`}
          ref={ref}>
          <DomainCell
            domain={row.domain}
            blurred={row.blurred}
            url={row.url}
            tags={row.tags}
          />
          <DataCell
            value={
              !row.spend_min && !row.spend_max
                ? '--'
                : `$${numberAbbreviation(row.spend_min, 0) || 0} - $${
                    numberAbbreviation(row.spend_max, 0) || 0
                  }`
            }
            blurred={row.blurred}
          />
          <DataCell
            value={row.employees ? numberAbbreviation(row.employees, 0) : '--'}
            blurred={row.blurred}
          />
          <DataCell
            value={row.rank ? numberAbbreviation(row.rank, 0) : '--'}
            blurred={row.blurred}
          />
          <DataCell value={getDaysAgo(row.updated_at)} blurred={row.blurred} />
        </TableRow>
        {showSnippets && snippet && (
          <SnippetRow
            domain={row.domain}
            snippet={snippet}
            columnsLength={columns.length}
          />
        )}
      </>
    )
  )
);

TableRowComponent.displayName = 'TableRowComponent';

export const SearchResultsTable = ({
  sites,
  total,
  onSortChange,
  sort,
  showSnippets,
  highlighting,
  order,
  sx,
  setPage,
  page,
}: SearchResultsTableProps) => {
  const user = useUser();
  const rows = React.useMemo(() => {
    if (!sites) {
      return [];
    }
    let hasFirstBlurredRow = false;
    return sites.map((site) => {
      const blurred = site.domain.indexOf('*') > -1;
      const row = {
        ...site,
        blurred,
        firstBlurred: hasFirstBlurredRow === false && blurred,
      };
      if (blurred) {
        hasFirstBlurredRow = true;
      }
      return row;
    });
  }, [sites]);

  const rowsPerPage = 25;

  const handleSortClick = useCallback(
    (field: keyof RowData) => {
      if (!isSortableField(field) || !isSortToggleable(field, sort)) {
        return;
      }

      if (sort === field) {
        onSortChange(`${field} ${order === 'asc' ? 'desc' : 'asc'}`);
      }
      // fields that are better sorted descending first
      else if (
        field === 'updated_at' ||
        field === 'spend_min' ||
        field === 'employees'
      ) {
        onSortChange(`${field} desc`);
      }
      // fields that are better sorted ascending first
      else {
        onSortChange(`${field} asc`);
      }
    },
    [onSortChange, sort, order]
  );

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const isSortableField = (field: keyof RowData): field is SortableFields => {
    return columns.find((cell) => cell.id === field)?.sortable || false;
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? rowsPerPage - rows.length : 0;

  const isBlurred = React.useMemo(() => {
    return rows.some((row) => row.blurred);
  }, [rows]);

  const firstBlurredRowRef = useRef<HTMLTableRowElement | null>(null);
  const [firstBlurredRowScrollTop, setFirstBlurredRowScrollTop] =
    React.useState<number>(250);

  React.useLayoutEffect(() => {
    if (firstBlurredRowRef.current) {
      setFirstBlurredRowScrollTop(firstBlurredRowRef.current.offsetTop);
    }
  }, [rows]);

  return (
    <Box
      sx={{ position: 'relative', width: '100%', ...sx }}
      className="search-results-table">
      <Paper sx={{ width: '100%', mb: 2, pr: 1, borderRadius: 0 }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table
            sx={{ tableLayout: 'auto', minWidth: 650, width: '100%' }}
            aria-labelledby="Results"
            size={'small'}>
            <TableHead>
              <TableRow>
                {columns.map((column, index) => {
                  const isClickable =
                    column.sortable && isSortToggleable(column.id, sort);
                  return (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? 'right' : 'left'}
                      padding={column.id === 'domain' ? 'normal' : 'none'}
                      sx={{
                        width: column.id === 'domain' ? 'auto' : 155,
                        minWidth: column.id === 'domain' ? 165 : undefined,
                      }}
                      sortDirection={sort === column.id ? order : false}>
                      <Tooltip
                        title={column.description}
                        PopperProps={{
                          sx: {
                            fontSize: '',
                          },
                        }}>
                        <TableSortLabel
                          active={sort === column.id}
                          direction={getSortDirection(column.id, sort, order)}
                          hideSortIcon={!column.sortable}
                          sx={{
                            fontWeight: 'bold',
                            cursor: isClickable ? 'pointer' : 'default',
                          }}
                          onClick={() => handleSortClick(column.id)}>
                          {column.icon && (
                            <SvgIcon
                              component={column.icon}
                              fontSize="small"
                              sx={{ mr: 1, ml: index === 0 ? 0 : 1 }}
                            />
                          )}
                          {column.name}
                          {sort === column.id ? (
                            <Box component="span" sx={{ ...visuallyHidden }}>
                              {order === 'desc'
                                ? 'sorted descending'
                                : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                return (
                  <TableRowComponent
                    key={`${row.domain}-${row.updated_at}`}
                    row={row}
                    showSnippets={showSnippets}
                    snippet={highlighting?.[row.domain] || null}
                    ref={
                      row.firstBlurred ? firstBlurredRowRef : React.createRef()
                    }
                  />
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 33 * emptyRows,
                  }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          slotProps={{
            spacer: {
              component: 'div',
              style: {
                display: 'flex',
                alignItems: 'center',
              },
              children: (
                <>
                  <LogoPoweredByLink color="text.secondary" />
                </>
              ),
            },
          }}
          component="div"
          count={total}
          page={page - 1}
          disabled={!total}
          onPageChange={(_event, newPage) => handleChangePage(newPage + 1)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </Paper>
      {isBlurred && (
        <BlurredUpgradeModal
          offsetTop={firstBlurredRowScrollTop}
          title={`🚀 ${
            user.isGuest
              ? 'Sign up to reveal more results'
              : 'Upgrade to reveal more results'
          }`}
          description={`${
            user.isGuest
              ? 'Register for a free account to reveal more results shown below. Some reports with fewer results require a paid subscription.'
              : 'Upgrade to reveal more results shown below. Some reports with fewer results require a paid subscription.'
          }`}
          reason="search.blurred"
        />
      )}
    </Box>
  );
};
